import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { subscribeToPush, unsubscribeFromPush } from '../services/pushNotifications';

interface User {
  id: number;
  username: string;
  email: string;
  role: 'admin' | 'doctor';
  first_name: string;
  last_name: string;
  doctor_first_name?: string;
  doctor_last_name?: string;
  doctor_specialization?: string;
  doctor_photo?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';
const AUTH_API_URL = API_BASE_URL.replace('/api/v1', '') + '/api/v1/auth';

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('access_token');
      if (token) {
        try {
          const response = await axios.get(`${AUTH_API_URL}/me/`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setUser(response.data);
        } catch (error) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
        }
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (username: string, password: string) => {
    try {
      const response = await axios.post(`${AUTH_API_URL}/login/`, {
        username,
        password,
      });

      const { access, refresh, role, email } = response.data;

      // Вход персонала гасит сессию пациента, и наоборот (см.
      // storeCabinetTokens). Одновременно в браузере живёт РОВНО ОДНА
      // личность: интерцептор api.ts предпочитает staff-токен
      // пациентскому, поэтому врач, вошедший потом пациентом, продолжал
      // ходить в ядро с правами врача — и получал в кабинете пациента
      // доступ к протоколу, который вправе подписывать только врач.
      localStorage.removeItem('patient_access_token');
      localStorage.removeItem('patient_refresh_token');

      localStorage.setItem('access_token', access);
      localStorage.setItem('refresh_token', refresh);

      setUser({
        id: 0,
        username,
        email,
        role,
        first_name: '',
        last_name: '',
      });

      const userResponse = await axios.get(`${AUTH_API_URL}/me/`, {
        headers: { Authorization: `Bearer ${access}` },
      });
      setUser(userResponse.data);

      // Подписываемся на Web Push (silent — не блокируем логин при ошибке)
      subscribeToPush().catch(() => {});
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    // Отписываемся от push при выходе (best-effort)
    unsubscribeFromPush().catch(() => {});
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
